/**
 * Utilities for identifying authentication-related "expected" errors
 * that should not be treated as real errors in logging/monitoring.
 */

/** HTTP status codes that represent expected auth flow states, not real errors */
export const AUTH_FLOW_STATUSES = {
  TWO_FA_REQUIRED: 428,
  TWO_FA_LOCKOUT: 429,
  TWO_FA_INVALID_CODE: 401,
} as const

/**
 * Checks whether a given status + context represents a 2FA-related operational state
 * (e.g. "2FA required" or "2FA lockout"), which should be logged at info/warn level
 * rather than error level.
 *
 * @param status - HTTP status code
 * @param hints - Optional additional context to help identify 2FA-related 429s
 */
export function is2FAOperationalStatus(
  status: number | undefined | null,
  hints?: {
    requires2FA?: boolean
    message?: string
    url?: string
  },
): boolean {
  if (status === AUTH_FLOW_STATUSES.TWO_FA_REQUIRED) {
    return true
  }

  if (status === AUTH_FLOW_STATUSES.TWO_FA_LOCKOUT) {
    const { requires2FA, message, url } = hints ?? {}
    return (
      !!requires2FA ||
      !!message?.includes('2FA') ||
      !!message?.includes('אימות דו-שלבי') ||
      !!url?.includes('2fa')
    )
  }

  if (status === AUTH_FLOW_STATUSES.TWO_FA_INVALID_CODE) {
    const { message, url } = hints ?? {}
    // For 401, we need additional context since it's a generic status code
    // Match on the specific Hebrew 2FA error message or a 2fa URL
    return !!message?.includes('קוד אימות דו-שלבי') || !!url?.includes('2fa')
  }

  // Also catch 2FA messages regardless of status code (e.g. if status is missing)
  const { message } = hints ?? {}
  if (message?.includes('קוד אימות דו-שלבי שגוי')) {
    return true
  }

  return false
}

/**
 * Checks whether a given status + context represents an expected/operational auth error
 * that should be downgraded from Error to Info or Warn in Logtail.
 *
 * @param status - HTTP status code
 * @param hints - Optional additional context
 * @returns { isOperational, suggestedLevel } — suggestedLevel is 'info' or 'warn'
 */
export function isOperationalAuthError(
  status: number | undefined | null,
  hints?: {
    message?: string
    requires2FA?: boolean
    requiresPasswordChange?: boolean
  },
): { isOperational: boolean; suggestedLevel: 'info' | 'warn' } {
  const NOT_OPERATIONAL = { isOperational: false, suggestedLevel: 'info' as const }

  // 428 — 2FA required or password change required: info
  if (status === 428) {
    return { isOperational: true, suggestedLevel: 'info' }
  }

  // 429 — 2FA lockout: warn (only when message is 2FA-related)
  if (status === 429) {
    const msg = hints?.message || ''
    if (msg.includes('אימות דו-שלבי') || msg.includes('2FA')) {
      return { isOperational: true, suggestedLevel: 'warn' }
    }
    return NOT_OPERATIONAL
  }

  // 403 — expected auth rejections (inactive user, IP block, system user, expired reset token): warn
  if (status === 403) {
    const msg = hints?.message || ''
    if (
      msg.includes('אינו פעיל') ||
      msg.includes('אינה מורשית') ||
      msg.includes('מערכת אינו מורשה') ||
      // Payload's reset-password token error — a routine "expired/reused link", not a fault.
      /token is either invalid or has expired/i.test(msg)
    ) {
      return { isOperational: true, suggestedLevel: 'warn' }
    }
    return NOT_OPERATIONAL
  }

  // 400 — logout with no user present: info (expected when session already expired)
  if (status === 400) {
    const msg = hints?.message || ''
    if (msg === 'No User') {
      return { isOperational: true, suggestedLevel: 'info' }
    }
    return NOT_OPERATIONAL
  }

  // 401 — expected credential-failure states: warn (not a real server error)
  //   - wrong 2FA code
  //   - unknown user / wrong password / any normalised login rejection (login-oracle fix #3789):
  //     these share the generic "email or password incorrect" message and are routine brute-force
  //     noise, so downgrade from Error to Warn to avoid flooding error monitoring.
  //   - account locked out (LockedAuth) after too many attempts — expected, self-clearing.
  if (status === 401) {
    const msg = hints?.message || ''
    if (
      msg.includes('קוד אימות דו-שלבי') ||
      msg.includes('הסיסמה שסופקו אינם נכונים') ||
      msg.includes('נעול') // "המשתמש נעול..." — account temporarily locked
    ) {
      return { isOperational: true, suggestedLevel: 'warn' }
    }
    return NOT_OPERATIONAL
  }

  return NOT_OPERATIONAL
}
